<template>
  <FinaroSelfEnrollment v-if="(submerchant && !submerchant.finaroId)"></FinaroSelfEnrollment>
  <div v-else class="row justify-content-center">
    <div class="col-12 col-lg-9 col-xl-7">
      <div class="card">
        <h2 class="card-header">Send a payment link</h2>

        <div class="card-body text-center" v-if="!submerchant">
          <img
            src="/static/img/illustrations/warning-sign.svg"
            class="p-5 w-50" />
          <p class="lead">Oops - we couldn't load your account.</p>
          <a
            class="btn btn-lg btn-info"
            href="javascript:window.location.reload();">Refresh this page <i class="fa fa-reload ml-2"></i></a>
        </div>

        <div class="card-body" v-else-if="submerchant && submerchant.finaroId">
          <form
            v-if="submerchantUser && submerchantUser.submerchantRole !== 'viewer'"
            @submit.prevent="createIntent">
            <!-- What the customer is paying for -->
            <div class="form-row">
              <div class="form-group col-12 col-sm-6">
                <label for="intent-amount">
                  Amount <span class="text-danger" title="Required">*</span>
                </label>
                <currency-input
                  id="intent-amount"
                  v-model="amount"
                  currency="GBP"
                  locale="gb"
                  :value-as-integer="true"
                  :allow-negative="false"
                  inputmode="decimal"
                  class="form-control"
                  placeholder="£0.00" />
              </div>

              <div class="form-group col-12 col-sm-6">
                <label for="intent-reference">
                  Reference <small class="text-muted">(optional)</small>
                </label>
                <input
                  id="intent-reference"
                  type="text"
                  v-model="reference"
                  maxlength="35"
                  placeholder="e.g. REF001"
                  class="form-control" />
              </div>

              <div class="form-group col-12">
                <label for="intent-description">
                  Description <small class="text-muted">(optional)</small>
                </label>
                <textarea
                  id="intent-description"
                  v-model="description"
                  rows="3"
                  class="form-control"
                  placeholder="e.g. Taxi journey to SE11 9FF"></textarea>
              </div>
            </div>

            <!-- How the link is sent -->
            <h6 class="text-muted mt-2 mb-3">Send the link</h6>

            <div class="form-row align-items-center mb-3">
              <div class="col-5 col-sm-3 d-flex align-items-center">
                <p-switch
                  v-model="sendCustomerSms"
                  type="primary"
                  role="switch"
                  tabindex="0"
                  aria-labelledby="intent-sms-label"
                  :aria-checked="sendCustomerSms ? 'true' : 'false'"
                  @keydown.native.space.prevent="sendCustomerSms = !sendCustomerSms"
                  @keydown.native.enter.prevent="sendCustomerSms = !sendCustomerSms"></p-switch>
                <span
                  id="intent-sms-label"
                  class="ml-2"
                  role="button"
                  @click="sendCustomerSms = !sendCustomerSms">By SMS</span>
              </div>
              <div v-if="sendCustomerSms" class="col-7 col-sm-9 form-group mb-0">
                <label class="sr-only">Mobile number</label>
                <vue-tel-input
                  name="customerPhoneNumber"
                  v-model="customerPhoneNumber"
                  :default-country="customerAddressCountryCode"
                  :preferredCountries="['gb', 'ie']"
                  mode="international"
                  placeholder="Mobile number"
                  input-classes="form-control"></vue-tel-input>
              </div>
            </div>

            <div class="form-row align-items-center mb-3">
              <div class="col-5 col-sm-3 d-flex align-items-center">
                <p-switch
                  v-model="sendCustomerEmail"
                  type="primary"
                  role="switch"
                  tabindex="0"
                  aria-labelledby="intent-email-label"
                  :aria-checked="sendCustomerEmail ? 'true' : 'false'"
                  @keydown.native.space.prevent="sendCustomerEmail = !sendCustomerEmail"
                  @keydown.native.enter.prevent="sendCustomerEmail = !sendCustomerEmail"></p-switch>
                <span
                  id="intent-email-label"
                  class="ml-2"
                  role="button"
                  @click="sendCustomerEmail = !sendCustomerEmail">By email</span>
              </div>
              <div v-if="sendCustomerEmail" class="col-7 col-sm-9 form-group mb-0">
                <label for="intent-email" class="sr-only">Email address</label>
                <input
                  id="intent-email"
                  class="form-control"
                  v-model="customerEmailAddress"
                  type="email"
                  autocomplete="off"
                  placeholder="Email address" />
              </div>
            </div>

            <!-- Less common fields, hidden by default -->
            <a
              href="#"
              role="button"
              class="d-inline-block py-2"
              :aria-expanded="showMoreOptions ? 'true' : 'false'"
              aria-controls="intent-more-options"
              @click.prevent="showMoreOptions = !showMoreOptions">
              <i class="fa fa-fw mr-1" :class="showMoreOptions ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
              {{ showMoreOptions ? "Fewer options" : "More options" }}
            </a>

            <div v-show="showMoreOptions" id="intent-more-options" class="border-top pt-3 mt-2">
              <div class="form-row">
                <div class="form-group col-12">
                  <label for="intent-customer-name">Customer name</label>
                  <input
                    id="intent-customer-name"
                    type="text"
                    v-model="customerName"
                    class="form-control"
                    placeholder="e.g. Stuart Jones" />
                </div>

                <div class="form-group col-12 col-sm-6">
                  <label for="intent-country">Customer country</label>
                  <select
                    id="intent-country"
                    v-model="customerAddressCountryCode"
                    class="form-control">
                    <option
                      v-for="country in countries"
                      :value="country.code"
                      :key="country.code">
                      {{ country.name }}
                    </option>
                  </select>
                </div>

                <div class="form-group col-12 col-sm-6">
                  <label for="intent-postcode">Customer postcode</label>
                  <input
                    id="intent-postcode"
                    type="text"
                    v-model="customerAddressPostcode"
                    class="form-control"
                    placeholder="e.g. E9 5AV" />
                </div>

                <div class="form-group col-12 col-sm-6">
                  <label for="intent-expiry">Link expires after</label>
                  <input
                    id="intent-expiry"
                    type="text"
                    v-model="expiryNaturalLanguage"
                    placeholder="14 days"
                    class="form-control" />
                  <!-- TODO: allow switching between specific date and free form input -->
                </div>

                <div class="form-group col-12 col-sm-6">
                  <label for="intent-capture-mode">Capture mode</label>
                  <select id="intent-capture-mode" v-model="captureMode" class="form-control">
                    <option value="automatic">Automatic (default)</option>
                    <option value="manual">Manual</option>
                  </select>
                </div>
              </div>
            </div>

            <div v-if="!working && error" class="alert alert-danger mt-3">
              <span>{{ error }}</span>
            </div>

            <button
              type="submit"
              class="btn btn-primary btn-lg btn-block mt-3"
              :disabled="working">
              <span v-if="!working">
                <span v-if="sendCustomerSms || sendCustomerEmail">
                  Send payment link
                  <span class="d-none d-sm-inline">
                    <span v-if="sendCustomerSms">by SMS</span>
                    <span v-if="sendCustomerSms && sendCustomerEmail"> and </span>
                    <span v-if="sendCustomerEmail">by email</span>
                  </span>
                </span>
                <span v-else>Create payment link</span>
                <i class="fa fa-paper-plane ml-2"></i>
              </span>
              <span v-else>Please wait&hellip; <i class="fa fa-spinner fa-spin ml-2"></i></span>
            </button>

            <p class="text-center text-muted small mt-3 mb-0">
              Don't need to send a link? Customers can pay any time with
              <router-link to="/scan-to-pay" class="text-nowrap">Scan to pay <i class="fa fa-qrcode ml-1"></i></router-link>
            </p>
          </form>

          <div v-else>
            <p class="text-warning">
              Sorry, you do not have sufficient permissions to create payment
              links for this account.
            </p>
          </div>
        </div>

        <div class="card-footer">
          <button v-on:click="$router.back()" class="btn btn-default">
            <i class="fa fa-arrow-left"></i>&nbsp; Go back
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VueQRCodeComponent from "vue-qrcode-component";
import FinaroSelfEnrollment from "./FinaroSelfEnrollment.vue";
import PSwitch from "src/components/UIComponents/Switch.vue";
import { apiErrorMessage } from "src/apiErrors";

export default {
  name: "IntentCreatePage",
  components: {
    qrCode: VueQRCodeComponent,
    FinaroSelfEnrollment,
    PSwitch,
  },
  data() {
    return {
      working: false,
      error: null,
      intent: null,
      customerName: null,
      sendCustomerSms: false,
      customerPhoneNumber: null,
      sendCustomerEmail: false,
      customerEmailAddress: null,
      customerAddressCountryCode: "GB",
      customerAddressPostcode: null,
      amount: null,
      description: null,
      reference: null,
      expiryNaturalLanguage: null,
      captureMode: "automatic",
      showMoreOptions: false,
      countries: [
        { name: "Albania", code: "AL" },
        { name: "Åland Islands", code: "AX" },
        { name: "Algeria", code: "DZ" },
        { name: "American Samoa", code: "AS" },
        { name: "Andorra", code: "AD" },
        { name: "Angola", code: "AO" },
        { name: "Anguilla", code: "AI" },
        { name: "Antarctica", code: "AQ" },
        { name: "Antigua and Barbuda", code: "AG" },
        { name: "Argentina", code: "AR" },
        { name: "Armenia", code: "AM" },
        { name: "Aruba", code: "AW" },
        { name: "Australia", code: "AU" },
        { name: "Austria", code: "AT" },
        { name: "Azerbaijan", code: "AZ" },
        { name: "Bahamas (the)", code: "BS" },
        { name: "Bahrain", code: "BH" },
        { name: "Bangladesh", code: "BD" },
        { name: "Barbados", code: "BB" },
        { name: "Belarus", code: "BY" },
        { name: "Belgium", code: "BE" },
        { name: "Belize", code: "BZ" },
        { name: "Benin", code: "BJ" },
        { name: "Bermuda", code: "BM" },
        { name: "Bhutan", code: "BT" },
        { name: "Bolivia (Plurinational State of)", code: "BO" },
        { name: "Bonaire, Sint Eustatius and Saba", code: "BQ" },
        { name: "Bosnia and Herzegovina", code: "BA" },
        { name: "Botswana", code: "BW" },
        { name: "Bouvet Island", code: "BV" },
        { name: "Brazil", code: "BR" },
        { name: "British Indian Ocean Territory (the)", code: "IO" },
        { name: "Brunei Darussalam", code: "BN" },
        { name: "Bulgaria", code: "BG" },
        { name: "Burkina Faso", code: "BF" },
        { name: "Burundi", code: "BI" },
        { name: "Cabo Verde", code: "CV" },
        { name: "Cambodia", code: "KH" },
        { name: "Cameroon", code: "CM" },
        { name: "Canada", code: "CA" },
        { name: "Cayman Islands (the)", code: "KY" },
        { name: "Central African Republic (the)", code: "CF" },
        { name: "Chad", code: "TD" },
        { name: "Chile", code: "CL" },
        { name: "China", code: "CN" },
        { name: "Christmas Island", code: "CX" },
        { name: "Cocos (Keeling) Islands (the)", code: "CC" },
        { name: "Colombia", code: "CO" },
        { name: "Comoros (the)", code: "KM" },
        { name: "Congo (the Democratic Republic of the)", code: "CD" },
        { name: "Congo (the)", code: "CG" },
        { name: "Cook Islands (the)", code: "CK" },
        { name: "Costa Rica", code: "CR" },
        { name: "Croatia", code: "HR" },
        { name: "Cuba", code: "CU" },
        { name: "Curaçao", code: "CW" },
        { name: "Cyprus", code: "CY" },
        { name: "Czechia", code: "CZ" },
        { name: "Côte d'Ivoire", code: "CI" },
        { name: "Denmark", code: "DK" },
        { name: "Djibouti", code: "DJ" },
        { name: "Dominica", code: "DM" },
        { name: "Dominican Republic (the)", code: "DO" },
        { name: "Ecuador", code: "EC" },
        { name: "Egypt", code: "EG" },
        { name: "El Salvador", code: "SV" },
        { name: "Equatorial Guinea", code: "GQ" },
        { name: "Eritrea", code: "ER" },
        { name: "Estonia", code: "EE" },
        { name: "Eswatini", code: "SZ" },
        { name: "Ethiopia", code: "ET" },
        { name: "Falkland Islands (the) [Malvinas]", code: "FK" },
        { name: "Faroe Islands (the)", code: "FO" },
        { name: "Fiji", code: "FJ" },
        { name: "Finland", code: "FI" },
        { name: "France", code: "FR" },
        { name: "French Guiana", code: "GF" },
        { name: "French Polynesia", code: "PF" },
        { name: "French Southern Territories (the)", code: "TF" },
        { name: "Gabon", code: "GA" },
        { name: "Gambia (the)", code: "GM" },
        { name: "Georgia", code: "GE" },
        { name: "Germany", code: "DE" },
        { name: "Ghana", code: "GH" },
        { name: "Gibraltar", code: "GI" },
        { name: "Greece", code: "GR" },
        { name: "Greenland", code: "GL" },
        { name: "Grenada", code: "GD" },
        { name: "Guadeloupe", code: "GP" },
        { name: "Guam", code: "GU" },
        { name: "Guatemala", code: "GT" },
        { name: "Guernsey", code: "GG" },
        { name: "Guinea", code: "GN" },
        { name: "Guinea-Bissau", code: "GW" },
        { name: "Guyana", code: "GY" },
        { name: "Haiti", code: "HT" },
        { name: "Heard Island and McDonald Islands", code: "HM" },
        { name: "Holy See (the)", code: "VA" },
        { name: "Honduras", code: "HN" },
        { name: "Hong Kong", code: "HK" },
        { name: "Hungary", code: "HU" },
        { name: "Iceland", code: "IS" },
        { name: "India", code: "IN" },
        { name: "Indonesia", code: "ID" },
        { name: "Iran (Islamic Republic of)", code: "IR" },
        { name: "Iraq", code: "IQ" },
        { name: "Ireland", code: "IE" },
        { name: "Isle of Man", code: "IM" },
        { name: "Israel", code: "IL" },
        { name: "Italy", code: "IT" },
        { name: "Jamaica", code: "JM" },
        { name: "Japan", code: "JP" },
        { name: "Jersey", code: "JE" },
        { name: "Jordan", code: "JO" },
        { name: "Kazakhstan", code: "KZ" },
        { name: "Kenya", code: "KE" },
        { name: "Kiribati", code: "KI" },
        { name: "Korea (the Democratic People's Republic of)", code: "KP" },
        { name: "Korea (the Republic of)", code: "KR" },
        { name: "Kuwait", code: "KW" },
        { name: "Kyrgyzstan", code: "KG" },
        { name: "Lao People's Democratic Republic (the)", code: "LA" },
        { name: "Latvia", code: "LV" },
        { name: "Lebanon", code: "LB" },
        { name: "Lesotho", code: "LS" },
        { name: "Liberia", code: "LR" },
        { name: "Libya", code: "LY" },
        { name: "Liechtenstein", code: "LI" },
        { name: "Lithuania", code: "LT" },
        { name: "Luxembourg", code: "LU" },
        { name: "Macao", code: "MO" },
        { name: "Madagascar", code: "MG" },
        { name: "Malawi", code: "MW" },
        { name: "Malaysia", code: "MY" },
        { name: "Maldives", code: "MV" },
        { name: "Mali", code: "ML" },
        { name: "Malta", code: "MT" },
        { name: "Marshall Islands (the)", code: "MH" },
        { name: "Martinique", code: "MQ" },
        { name: "Mauritania", code: "MR" },
        { name: "Mauritius", code: "MU" },
        { name: "Mayotte", code: "YT" },
        { name: "Mexico", code: "MX" },
        { name: "Micronesia (Federated States of)", code: "FM" },
        { name: "Moldova (the Republic of)", code: "MD" },
        { name: "Monaco", code: "MC" },
        { name: "Mongolia", code: "MN" },
        { name: "Montenegro", code: "ME" },
        { name: "Montserrat", code: "MS" },
        { name: "Morocco", code: "MA" },
        { name: "Mozambique", code: "MZ" },
        { name: "Myanmar", code: "MM" },
        { name: "Namibia", code: "NA" },
        { name: "Nauru", code: "NR" },
        { name: "Nepal", code: "NP" },
        { name: "Netherlands (the)", code: "NL" },
        { name: "New Caledonia", code: "NC" },
        { name: "New Zealand", code: "NZ" },
        { name: "Nicaragua", code: "NI" },
        { name: "Niger (the)", code: "NE" },
        { name: "Nigeria", code: "NG" },
        { name: "Niue", code: "NU" },
        { name: "Norfolk Island", code: "NF" },
        { name: "Northern Mariana Islands (the)", code: "MP" },
        { name: "Norway", code: "NO" },
        { name: "Oman", code: "OM" },
        { name: "Pakistan", code: "PK" },
        { name: "Palau", code: "PW" },
        { name: "Palestine, State of", code: "PS" },
        { name: "Panama", code: "PA" },
        { name: "Papua New Guinea", code: "PG" },
        { name: "Paraguay", code: "PY" },
        { name: "Peru", code: "PE" },
        { name: "Philippines (the)", code: "PH" },
        { name: "Pitcairn", code: "PN" },
        { name: "Poland", code: "PL" },
        { name: "Portugal", code: "PT" },
        { name: "Puerto Rico", code: "PR" },
        { name: "Qatar", code: "QA" },
        { name: "Republic of North Macedonia", code: "MK" },
        { name: "Romania", code: "RO" },
        { name: "Russian Federation (the)", code: "RU" },
        { name: "Rwanda", code: "RW" },
        { name: "Réunion", code: "RE" },
        { name: "Saint Barthélemy", code: "BL" },
        { name: "Saint Helena, Ascension and Tristan da Cunha", code: "SH" },
        { name: "Saint Kitts and Nevis", code: "KN" },
        { name: "Saint Lucia", code: "LC" },
        { name: "Saint Martin (French part)", code: "MF" },
        { name: "Saint Pierre and Miquelon", code: "PM" },
        { name: "Saint Vincent and the Grenadines", code: "VC" },
        { name: "Samoa", code: "WS" },
        { name: "San Marino", code: "SM" },
        { name: "Sao Tome and Principe", code: "ST" },
        { name: "Saudi Arabia", code: "SA" },
        { name: "Senegal", code: "SN" },
        { name: "Serbia", code: "RS" },
        { name: "Seychelles", code: "SC" },
        { name: "Sierra Leone", code: "SL" },
        { name: "Singapore", code: "SG" },
        { name: "Sint Maarten (Dutch part)", code: "SX" },
        { name: "Slovakia", code: "SK" },
        { name: "Slovenia", code: "SI" },
        { name: "Solomon Islands", code: "SB" },
        { name: "Somalia", code: "SO" },
        { name: "South Africa", code: "ZA" },
        { name: "South Georgia and the South Sandwich Islands", code: "GS" },
        { name: "South Sudan", code: "SS" },
        { name: "Spain", code: "ES" },
        { name: "Sri Lanka", code: "LK" },
        { name: "Sudan (the)", code: "SD" },
        { name: "Suriname", code: "SR" },
        { name: "Svalbard and Jan Mayen", code: "SJ" },
        { name: "Sweden", code: "SE" },
        { name: "Switzerland", code: "CH" },
        { name: "Syrian Arab Republic", code: "SY" },
        { name: "Taiwan (Province of China)", code: "TW" },
        { name: "Tajikistan", code: "TJ" },
        { name: "Tanzania, United Republic of", code: "TZ" },
        { name: "Thailand", code: "TH" },
        { name: "Timor-Leste", code: "TL" },
        { name: "Togo", code: "TG" },
        { name: "Tokelau", code: "TK" },
        { name: "Tonga", code: "TO" },
        { name: "Trinidad and Tobago", code: "TT" },
        { name: "Tunisia", code: "TN" },
        { name: "Turkey", code: "TR" },
        { name: "Turkmenistan", code: "TM" },
        { name: "Turks and Caicos Islands (the)", code: "TC" },
        { name: "Tuvalu", code: "TV" },
        { name: "Uganda", code: "UG" },
        { name: "Ukraine", code: "UA" },
        { name: "United Arab Emirates (the)", code: "AE" },
        { name: "United Kingdom", code: "GB" },
        { name: "United States Minor Outlying Islands (the)", code: "UM" },
        { name: "United States of America (the)", code: "US" },
        { name: "Uruguay", code: "UY" },
        { name: "Uzbekistan", code: "UZ" },
        { name: "Vanuatu", code: "VU" },
        { name: "Venezuela (Bolivarian Republic of)", code: "VE" },
        { name: "Viet Nam", code: "VN" },
        { name: "Virgin Islands (British)", code: "VG" },
        { name: "Virgin Islands (U.S.)", code: "VI" },
        { name: "Wallis and Futuna", code: "WF" },
        { name: "Western Sahara", code: "EH" },
        { name: "Yemen", code: "YE" },
        { name: "Zambia", code: "ZM" },
        { name: "Zimbabwe", code: "ZW" },
      ],
    };
  },
  computed: {
    submerchant: function () {
      return this.$store.state.submerchant;
    },
    submerchantUser: function () {
      return this.$store.state.submerchantUser;
    },
    anytimeLink: function () {
      const baseUrl = "https://pay.cabcard.services";
      return `${baseUrl}/now?s=${this.submerchant.id}`;
    },
  },
  beforeMount() {
    this.maybeReloadSubmerchant();
    this.loading = true;
    this.axios
      .get("/v1/submerchants", {
        headers: {
          "x-apicache-bypass": "true", // bypass API cache to get accessible submerchants
        },
      })
      .then((response) => {
        this.loading = false;
        this.submerchants = response.data;
      })
      .catch((error) => {
        this.loading = false;
        this.loadingError = "Failed to load submerchants";
      });
  },
  methods: {
    maybeReloadSubmerchant() {
      if (!this.$store.state.submerchant.finaroId) {
        this.axios
          .get(`/v1/submerchants/${this.$store.state.submerchant.id}`, {
            headers: {
              "x-apicache-bypass": "true", // bypass API cache to get latest
            },
          })
          .then((response) => {
            const submerchant = response.data;
            this.$store.commit("SET_SUBMERCHANT", submerchant);
          })
          .catch((error) => {
            console.error("Error loading submerchant", error.message);
          });
      }
    },
    createIntent: function () {
      this.error = null; // clear any existing errors
      // some validation
      if (!this.amount) {
        this.error = "Amount is required";
      }

      if (this.sendCustomerSms && !this.customerPhoneNumber) {
        this.error = "Customer phone number is required to send SMS";
      }

      if (this.sendCustomerEmail && !this.customerEmailAddress) {
        this.error = "Customer email address is required to send email";
      }

      if (this.reference && this.reference.length > 35) {
        this.error = "Reference must be 35 characters or fewer";
      }

      if (this.error) {
        // SweetAlert
        this.$swal({
          title: "Oops...",
          text: this.error,
          icon: "error",
        });
        return;
      }

      // treat as valid
      this.working = true;

      const requestBody = {
        saleAmount: this.amount,
        currency: "gbp",
        submerchantId: this.submerchant.id,
        description: this.description,
        reference: this.reference,
        customerName: this.customerName,
        brandIdentifier: this.submerchant.id,
        captureMode: this.captureMode,
      };

      if (this.customerPhoneNumber && this.sendCustomerSms) {
        requestBody.customerPhoneNumber = this.customerPhoneNumber;
        requestBody.customerPhoneNumber = requestBody.customerPhoneNumber
          .split("-")
          .join(""); // remove dashes
        requestBody.customerPhoneNumber = requestBody.customerPhoneNumber
          .split(" ")
          .join(""); // remove spaces
        requestBody.sendCustomerSms = true;
      }

      if (this.customerEmailAddress && this.sendCustomerEmail) {
        requestBody.customerEmailAddress = this.customerEmailAddress;
        requestBody.sendCustomerEmail = true;
      }

      if (this.expiryNaturalLanguage) {
        requestBody.expiryNaturalLanguage = this.expiryNaturalLanguage;
      }

      if (this.customerAddressCountryCode) {
        if (!requestBody.customerAddress) {
          requestBody.customerAddress = {};
        }
        requestBody.customerAddress.countryCode =
          this.customerAddressCountryCode;
      }

      if (this.customerAddressPostcode) {
        if (!requestBody.customerAddress) {
          requestBody.customerAddress = {};
        }
        requestBody.customerAddress.postcode = this.customerAddressPostcode;
      }

      this.axios
        .post("/v1/intents", requestBody)
        .then((response) => {
          this.working = false;
          this.intent = response.data;

          this.$notify({
            message: "Payment link created",
            timeout: 3000,
            icon: "fa fa-check",
            horizontalAlign: "right",
            verticalAlign: "top",
            type: "success",
          });

          this.$router.push(`/payment-links/${this.intent.id}`);
        })
        .catch((error) => {
          this.working = false;
          console.error("Error: ", error);
          this.error = apiErrorMessage(error);
        });
    },
    onCopySuccess() {
      this.$notify({
        message: "Copied to clipboard",
        timeout: 1000,
        icon: "fa fa-copy",
        horizontalAlign: "right",
        verticalAlign: "top",
        type: "success",
      });
    },
    onCopyError() {
      this.$notify({
        message: "Could not copy to clipboard",
        timeout: 1000,
        icon: "fa fa-exclamation-triangle",
        horizontalAlign: "right",
        verticalAlign: "top",
        type: "error",
      });
    },
  },
};
</script>
